'use client';

import { useLayoutEffect, useMemo, useState } from 'react';
import { ResetIcon } from '@radix-ui/react-icons';
import {
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Node,
  type Edge,
  Position,
  MarkerType,
  useReactFlow,
  Controls,
  ControlButton,
} from '@xyflow/react';
import dagre from 'dagre';
import '@xyflow/react/dist/style.css';
import { BezierEdge } from './edge';
import { EntityNode, BybitWalletNode, HackerWalletsNode } from './node';
import type { EntityListResp } from '@/types/home';
import { formatNumber } from '@web3/base-utils';
import { useFm } from '@web3/base-hooks';

// 定义布局函数
const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction = 'LR'
) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const isHorizontal = direction === 'LR';
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: Math.max(50, Math.min(200, 800 / nodes.length)), // 根据节点数动态计算间距,最小 50 最大 200
    ranksep: 200,
    marginx: 20,
    marginy: 20,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 90, height: 45 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 90,
        y: nodeWithPosition.y,
      },
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
    };
  });

  // 计算所有节点的边界
  const minX = Math.min(...layoutedNodes.map((node) => node.position.x));
  const maxX = Math.max(...layoutedNodes.map((node) => node.position.x));
  const minY = Math.min(...layoutedNodes.map((node) => node.position.y));
  const maxY = Math.max(...layoutedNodes.map((node) => node.position.y));

  // 计算图的总宽度和高度
  const graphWidth = maxX - minX;
  const graphHeight = maxY - minY;

  // 获取容器宽度和高度
  const containerWidth = window.innerWidth * 0.66; // 因为容器宽度是2/3
  const containerHeight = 720; // 固定高度为720px

  // 计算居中需要的偏移量
  const offsetX = (containerWidth - graphWidth) / 2 - minX;
  const offsetY = (containerHeight - graphHeight) / 2 - minY;

  // 应用横向偏移和居中偏移
  return layoutedNodes.map((node, index) => ({
    ...node,
    position: {
      x: node.position.x + offsetX + (index >= 2 ? 200 : 0),
      y: node.position.y + offsetY,
    },
  }));
};

// 生成初始节点和边
const generateInitialElements = (entityList: EntityListResp[]) => {
  if (!entityList || entityList.length === 0) {
    return { nodes: [], edges: [] };
  }

  const defaultPosition = { x: 0, y: 0 };
  const useDefaultEdge = entityList.length <= 1; // 当实体数量小于等于1时（总节点数<=3）使用直线

  const nodes: Node[] = [
    {
      id: 'Bybit',
      data: { label: 'Bybit Wallet' },
      type: 'bybitWallet',
      position: defaultPosition,
    },
    {
      id: 'Hacker',
      data: { label: 'Hacker Wallets' },
      type: 'hackerWallets',
      position: defaultPosition,
    },
    ...entityList.map((entity, index) => ({
      id: `target-${index}`,
      data: entity,
      type: 'entity',
      position: defaultPosition,
    })),
  ];

  const edges: Edge[] = [
    {
      id: 'bybit-to-hacker',
      source: 'Bybit',
      target: 'Hacker',
      type: useDefaultEdge ? 'default' : 'bezier',
      label: '$1.4B',
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 10,
        height: 10,
        color: '#ADB1B8',
      },
      style: {
        stroke: '#ADB1B8',
        strokeWidth: 2,
      },
      data: {
        selectColor: '#ff0072',
      },
    },
    ...entityList.map((entity, index) => ({
      id: `hacker-to-target-${index}`,
      source: 'Hacker',
      target: `target-${index}`,
      type: useDefaultEdge ? 'default' : 'bezier',
      label: isNaN(Number(entity.received))
        ? '0'
        : formatNumber(Number(entity.received), {
            kmb: true,
            kmbDecimal: 1,
          }),
      labelBgStyle: { fill: '#1a1a1a' },
      labelStyle: { fill: '#fff', fontSize: 12, fontWeight: 500 },
      markerEnd: {
        type: MarkerType.Arrow,
        width: 10,
        height: 10,
        color: '#ADB1B8',
      },
      style: {
        stroke: '#ADB1B8',
        strokeWidth: 2,
      },
      animated: true,
      data: {
        selectColor: '#ff0072',
      },
    })),
  ];

  return { nodes, edges };
};

const edgeTypes = {
  bezier: BezierEdge,
};

export default function Flow({
  onNodeClick,
  entityList,
  curEntity,
}: {
  onNodeClick?: (entity: EntityListResp) => void;
  entityList: EntityListResp[];
  curEntity: EntityListResp;
}) {
  const fm = useFm();
  const [isHovered, setIsHovered] = useState(false);
  const { nodes: initialNodes, edges: initialEdges } =
    generateInitialElements(entityList);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { fitView } = useReactFlow();

  const [currentTime] = useState(
    new Date().toLocaleString('zh-CN', { hour12: false })
  );

  useLayoutEffect(() => {
    if (!entityList || entityList.length === 0) return;
    const { nodes: newNodes, edges: newEdges } =
      generateInitialElements(entityList);
    const layoutedNodes = getLayoutedElements(newNodes, newEdges);
    setNodes(layoutedNodes);
    setEdges(newEdges);

    // 在节点布局完成后调用 fitView
    setTimeout(() => {
      fitView();
    }, 0);
  }, [entityList, setNodes, setEdges, fitView]);

  const nodeTypes = useMemo(
    () => ({
      bybitWallet: BybitWalletNode,
      hackerWallets: HackerWalletsNode,
      entity: (props) => <EntityNode {...props} curEntity={curEntity} />,
    }),
    [curEntity]
  );

  return (
    <ReactFlow
      style={{
        backgroundColor: isHovered ? '#1E2023' : '#101014',
        transition: 'background-color 0.3s ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      colorMode="dark"
      nodes={nodes}
      nodeTypes={nodeTypes}
      onNodeClick={(event: React.MouseEvent, node: Node) => {
        if (node.type !== 'entity') return;
        onNodeClick?.(node.data as EntityListResp);
      }}
      onNodesChange={onNodesChange}
      edges={edges}
      edgeTypes={edgeTypes}
      onEdgesChange={onEdgesChange}
      proOptions={{ hideAttribution: true }}
      draggable
      minZoom={0.3}
    >
      <Controls
        orientation="horizontal"
        showInteractive={false}
        showFitView={false}
      >
        <ControlButton onClick={() => fitView()}>
          <ResetIcon />
        </ControlButton>
        <div>
          <span className="text-[#ADB1B8] font-ibm-plex-sans text-xs font-medium ml-4">
            {fm('lastUpdatedAt')}
          </span>
          <span className="text-[#FFF] font-ibm-plex-sans text-xs font-medium mr-4">
            {' '}
            {currentTime}
          </span>
        </div>
      </Controls>
    </ReactFlow>
  );
}
